/**
 * dedentCode — textwrap.dedent semantics in TypeScript.
 *
 * Removes the longest common leading whitespace run shared by every
 * non-empty line. Empty lines are ignored when computing the common
 * prefix but preserved verbatim in the output. Single-line input and
 * input with no common indent are returned unchanged.
 *
 * Tabs and spaces are compared by character, not visual width — this
 * matches Python's `textwrap.dedent` and keeps the implementation
 * predictable for mixed indentation inputs.
 *
 * sl-93dd.1: Single source of truth for every auto-indent fix
 * (paste handler, save normalizer, manual Format button).
 */

/**
 * Match the longest whitespace-only prefix of a string. Returns the
 * prefix verbatim; for lines with no leading whitespace returns "".
 */
function leadingWhitespace(line: string): string {
  const match = line.match(/^[ \t]*/);
  return match ? match[0] : '';
}

/** Longest common prefix between two strings (character-wise). */
function commonPrefix(a: string, b: string): string {
  const len = Math.min(a.length, b.length);
  let i = 0;
  while (i < len && a[i] === b[i]) i++;
  return a.slice(0, i);
}

export function dedentCode(code: string): string {
  if (!code) return code;

  const lines = code.split('\n');

  // Compute the shared whitespace prefix across all non-empty lines.
  let shared: string | null = null;
  for (const line of lines) {
    if (line.length === 0) continue;
    const prefix = leadingWhitespace(line);
    // Skip whitespace-only lines — they inflate the "shared" answer to
    // their own length and would cause no dedent at all.
    if (prefix.length === line.length) continue;
    shared = shared === null ? prefix : commonPrefix(shared, prefix);
    if (shared === '') break;
  }

  if (!shared) return code;

  return lines
    .map(line => (line.startsWith(shared) ? line.slice(shared.length) : line))
    .join('\n');
}
